import Stripe from "stripe";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { onUserDataChange, validateSignInUser } from "../../services/user";
import { useEffect, useMemo, useState } from "react";
import _, { debounce, filter, isEmpty, isNull, sumBy } from "lodash";
import { UserPowerUps, User as WLUser } from "../../../Common/models/user";
import { shallow } from "zustand/shallow";
import { useWebUserStore } from "./users";
import { bugNotif } from "../../api/bugsnag";
import { User } from "firebase/auth";
import { useWLLoaderStore } from "../../../Common/components/WLLoader/store";
import {
  useIonRouter,
  useIonViewDidEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import {
  WEB_CANCEL_SUBSCRIPTION,
  WEB_OTP,
  WEB_PAYMENT,
} from "../../constants/routes";
import { WLStripeWPlanSubscription } from "../../../Common/models/subscriptions";
import { isStripePaused } from "../../../Common/utils/subscriptionPause";
import { getErrorMsg } from "../../../Common/utils/common";
import {
  getActiveSubscription,
  getUserLatestFailedPaymentAttempt,
} from "../../api/stripe";
import { getUserPowerUps } from "../../api/user";
import useSuperGiveAwayStore from "../giveaways/store";
import useSuccessRetryPaymentListenerStore from "../../pages/PurchaseWeb/PaymentV2/SuccessPaymentListeners/SuccessRetryPaymentListener/store";

let unsubscribeUser = () => {};
export const FETCH_USER_POWER_UPS_QUERY_KEY = "FETCH_USER_POWERUPS";

const buildWebOtpNavigationPath = (
  pathname: string,
  search: string | undefined,
): string => {
  const normalizedSearch =
    search && search.startsWith("?") ? search.slice(1) : search ?? "";
  const params = new URLSearchParams(normalizedSearch);
  params.delete("verificationType");

  if (pathname === WEB_PAYMENT && params.get("mode") === "retryPayment") {
    params.set("verificationType", "fixPaymentVerification");
  } else if (pathname === WEB_CANCEL_SUBSCRIPTION) {
    params.set("verificationType", "cancellationVerification");
  }

  const query = params.toString();
  return WEB_OTP + (query ? `?${query}` : "");
};

export const useWebUserProvider = ({ userId }: { userId: string }) => {
  const router = useIonRouter();
  const queryClient = useQueryClient();
  const [
    user,
    setUser,
    setUserPhoneNumber,
    setSignInOtpFirstName,

    stripeActiveSubscription,
    setStripeActiveSubscription,

    setUserPowerUpsHistory,

    userPowerUps,
    setUserPowerUps,

    powerUpEntries,
    setPowerUpEntries,
    setTotalEntries,
    setAccumulatedEntries,
    accumulatedEntries,

    resetWebUsersStore,
  ] = useWebUserStore(
    (store) => [
      store.user,
      store.setUser,
      store.setUserPhoneNumber,
      store.setSignInOtpFirstName,

      store.stripeActiveSubscription,
      store.setStripeActiveSubscription,

      store.setUserPowerUpsHistory,

      store.userPowerUps,
      store.setUserPowerUps,

      store.powerUpEntries,
      store.setPowerUpEntries,
      store.setTotalEntries,
      store.setAccumulatedEntries,
      store.accumulatedEntries,

      store.resetWebUsersStore,
    ],
    shallow
  );

  const [ongoingGiveaway] = useSuperGiveAwayStore((state) => [
    state.ongoingGiveaway,
  ]);

  const {
    data: respValidateAuthUser,
    error: errorAuthUser,
    isLoading: loadingAuthUser,
    refetch: refetchAuthUser,
  } = useQuery({
    queryKey: ["GET_AUTH_USER", userId],
    queryFn: async () => {
      if (!userId) return null;
      const resp = await validateSignInUser({ userId });
      return resp ? resp : null;
    },
  });

  const [isLoadingWLUser, setIsLoadingWLUser] = useState(true);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [inValidUserRequest, setInvalidUserRequest] = useState(false);

  const handleUserChange = debounce(async (user: WLUser, error) => {
    if (!isEmpty(error)) {
      resetWebUsersStore();
      bugNotif("initializeUser - onUserDataChange", error || "");
    } else {
      if (!isEmpty(user)) {
        setUser(user);
      } else {
        resetWebUsersStore();
      }
    }
    setIsLoadingWLUser(false);
  }, 500);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const setLoading = (loading: boolean) => setIsLoadingWLUser(loading);
    const setInvalid = (invalid: boolean) => setInvalidUserRequest(invalid);

    if (loadingAuthUser) {
      setInvalid(false);
      setLoading(true);
      return;
    }

    if (errorAuthUser) {
      setInvalid(true);
      setLoading(false);
      return;
    }

    if (!respValidateAuthUser) {
      setInvalid(true);
      setLoading(false);
      return;
    }

    const respUser = respValidateAuthUser as {
      valid?: boolean;
      phoneNumber?: string;
      firstName?: string;
    };
    const authUser = respValidateAuthUser as User;

    if (respUser.valid === false && !respUser.phoneNumber) {
      setInvalid(true);
      setLoading(false);
    } else if (!authUser?.uid && respUser.phoneNumber) {
      setUserPhoneNumber(respUser.phoneNumber);
      setSignInOtpFirstName(respUser.firstName?.trim() || null);
      router.push(
        buildWebOtpNavigationPath(
          router.routeInfo.pathname,
          router.routeInfo.search,
        ),
        "forward",
        "push",
      );
      setInvalid(false);
      setLoading(false);
    } else if (authUser?.uid) {
      unsubscribe = onUserDataChange(authUser.uid, handleUserChange);
      setInvalid(false);
      setLoading(false);
    } else {
      setInvalid(true);
      setLoading(false);
    }

    return () => unsubscribe?.();
  }, [respValidateAuthUser, loadingAuthUser, errorAuthUser]);

  // Existing stripe user subscription
  const {
    data: userActiveStripeSubscription,
    error: activeStripeSubscriptionError,
    isLoading: loadingActiveStripeSubscription,
  } = useQuery({
    queryKey: [
      "FETCH_USER_STRIPE_ACTIVE_SUBSCRIPTION",
      user?.stripeId,
      user?.latestStripeEvent,
      user?.latestStripeSubscriptionUpdate,
    ],
    queryFn: async () => {
      if (!user || user.stripeId === undefined) {
        return;
      }
      const resp = await getActiveSubscription(user.stripeId);
      return resp;
    },
    enabled: !!user && !!user.stripeId,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!isNull(activeStripeSubscriptionError)) {
      setStripeActiveSubscription(null);
      bugNotif(
        "FETCH_USER_STRIPE_ACTIVE_SUBSCRIPTION",
        getErrorMsg(activeStripeSubscriptionError).rawErrorMsg
      );
    }
  }, [activeStripeSubscriptionError]);

  useEffect(() => {
    if (userActiveStripeSubscription !== undefined) {
      setStripeActiveSubscription(
        userActiveStripeSubscription.data
          .result as WLStripeWPlanSubscription | null
      );
    }
  }, [userActiveStripeSubscription]);

  const isActiveStripeSubscriptionLoading = useMemo(() => {
    if (!user?.stripeId) {
      return false;
    }

    return (
      loadingActiveStripeSubscription || stripeActiveSubscription === undefined
    );
  }, [
    loadingActiveStripeSubscription,
    stripeActiveSubscription,
    user?.stripeId,
  ]);

  // --- End of existing stripe user subscription ---

  // POWER UPS ---

  let refetchPowerupsWaitTime = 1000;
  // Entries computation handling
  const {
    data: userPowerUpList,
    error: userPowerUpsError,
    isLoading: loadingPowerUpsList,
  } = useQuery({
    queryKey: [
      FETCH_USER_POWER_UPS_QUERY_KEY,
      user ? user.stripeId || "native-user" : "no-user",
      user ? user.latestRefreshDt || "no-refresh" : "no-user",
      user
        ? user.latestStripeInvoiceEvent
          ? user.latestStripeInvoiceEvent.id
          : ""
        : "",
      user?.claimedPowerUpForCancelling || false,
    ],
    queryFn: async () => {
      if (!user || user.stripeId === undefined) {
        return null;
      }
      const resp = await getUserPowerUps(user.docId);
      return resp;
    },
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!userPowerUpList) {
      return;
    }

    const powerUps = userPowerUpList.data.result as UserPowerUps[];
    setUserPowerUpsHistory(powerUps);

    if (!isNull(ongoingGiveaway)) {
      setUserPowerUps(
        filter(
          powerUps,
          (powerUp) => powerUp.giveaway.docId === ongoingGiveaway.docId
        )
      );
    }

    refetchPowerupsWaitTime = 1000;
  }, [userPowerUpList, ongoingGiveaway]);

  useEffect(() => {
    if (!isNull(userPowerUpsError)) {
      bugNotif(
        "FETCH_USER_POWERUPS",
        getErrorMsg(userPowerUpsError).rawErrorMsg
      );

      //retrying
      retryFetchPowerUps();
    }
  }, [userPowerUpsError]);

  const retryFetchPowerUps = async () => {
    if (refetchPowerupsWaitTime / 1000 < 60) {
      await new Promise((resolve) => {
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: [FETCH_USER_POWER_UPS_QUERY_KEY],
          });
          resolve(true);
        }, refetchPowerupsWaitTime);
      });

      refetchPowerupsWaitTime *= 2;
    }
  };

  const hasSubscription = useMemo(() => {
    return !!user && (user.stripeId !== undefined || !!user.subscription); // Show entries for stripe users without subscription
  }, [user?.subscription]);

  const oneOffProducts = useMemo(() => {
    return !!user ? user.oneOffProducts || null : null;
  }, [user?.updatedDt]);

  const oldPowerUpSource = useMemo(() => {
    return hasSubscription && !isNull(oneOffProducts) ? oneOffProducts : [];
  }, [hasSubscription, oneOffProducts]);

  const currentOldSourceGiveawayOneOffs = useMemo(() => {
    return hasSubscription && !isNull(ongoingGiveaway)
      ? filter(
          oldPowerUpSource,
          (ticket) => ticket.giveAway.docId === ongoingGiveaway.docId
        )
      : [];
  }, [hasSubscription, ongoingGiveaway, oldPowerUpSource]);

  useEffect(() => {
    const oldSourcePowerUpEntries = sumBy(
      currentOldSourceGiveawayOneOffs,
      (oneOff) => oneOff.entries
    );

    const newSource = hasSubscription
      ? sumBy(userPowerUps, (powerUp) => powerUp.entries)
      : 0;

    setPowerUpEntries(newSource + oldSourcePowerUpEntries);
  }, [
    currentOldSourceGiveawayOneOffs,
    userPowerUps,
    ongoingGiveaway,
    hasSubscription,
  ]);

  const userEntries = useMemo(() => {
    return !!user ? user.entryPoints : 0;
  }, [user?.entryPoints]);

  useEffect(() => {
    setAccumulatedEntries(userEntries);
  }, [userEntries]);

  useEffect(() => {
    setTotalEntries(accumulatedEntries + powerUpEntries);
  }, [accumulatedEntries, powerUpEntries]);

  const { setLoading } = useWLLoaderStore();

  useIonViewDidEnter(() => {
    refetchAuthUser();
  }, []);

  useIonViewWillLeave(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    setIsLoadingProviders(loadingAuthUser || isLoadingWLUser);
    setLoading(loadingAuthUser || isLoadingWLUser);
  }, [loadingAuthUser, isLoadingWLUser]);

  return {
    isLoadingProviders,
    isActiveStripeSubscriptionLoading,
    loadingAuthUser,
    errorAuthUser,
    inValidUserRequest,
  };
};

export const useWebUserPaymentProvider = () => {
  const [
    user,

    stripeActiveSubscription,

    latestFailedStripePaymentAttempt,
    setLatestFailedStripePaymentAttempt,

    setHasBillingIssue,
  ] = useWebUserStore(
    (store) => [
      store.user,

      store.stripeActiveSubscription,

      store.latestFailedStripePaymentAttempt,
      store.setLatestFailedStripePaymentAttempt,

      store.setHasBillingIssue,
    ],
    shallow
  );

  // Existing stripe latest failed payment attempt
  const {
    data: userLatestFailedStripePaymentAttempt,
    error: latestFailedStripePaymentAttemptError,
  } = useQuery({
    queryKey: [
      "FETCH_USER_LATEST_FAILED_STRIPE_PAYMENT_ATTEMPT",
      user?.stripeId,
      user?.latestStripeEvent,
      user?.latestStripeSubscriptionUpdate,
    ],
    queryFn: async () => {
      if (!user || user.stripeId === undefined) {
        return undefined;
      }
      const resp = await getUserLatestFailedPaymentAttempt(user.docId);
      return resp;
    },
    enabled: !!user && !!user.stripeId,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!isNull(latestFailedStripePaymentAttemptError)) {
      setLatestFailedStripePaymentAttempt(null);
      console.log(
        "error on latest failed stripe payment attempt",
        latestFailedStripePaymentAttemptError
      );
      bugNotif(
        "FETCH_USER_LATEST_FAILED_STRIPE_PAYMENT_ATTEMPT",
        getErrorMsg(latestFailedStripePaymentAttemptError).rawErrorMsg
      );
    }
  }, [latestFailedStripePaymentAttemptError]);

  useEffect(() => {
    if (userLatestFailedStripePaymentAttempt !== undefined) {
      setLatestFailedStripePaymentAttempt(
        userLatestFailedStripePaymentAttempt.data.result
      );
    }
  }, [userLatestFailedStripePaymentAttempt]);

  const checkForBillingIssue = async (
    user: WLUser,
    activeStripeSubscription: WLStripeWPlanSubscription
  ) => {
    let hasBillingIssue = false;

    if (
      user.stripeId !== undefined &&
      user.latestStripeEvent !== undefined &&
      !!user.subscription
    ) {
      const userActiveSubscription = activeStripeSubscription;

      // Any pause (incl. 14-day extension) suppresses a false billing issue here.
      const subscriptionPaused = isStripePaused(userActiveSubscription);
      if (
        !isNull(userActiveSubscription) &&
        userActiveSubscription.status === "past_due" &&
        !subscriptionPaused
      ) {
        hasBillingIssue = true;
      }
    }

    setHasBillingIssue(hasBillingIssue);
  };

  useEffect(() => {
    if (!!user && stripeActiveSubscription) {
      checkForBillingIssue(user, stripeActiveSubscription);
    }
  }, [
    user?.latestStripeEvent?.type,
    user?.latestRevCatEvent?.event.type,
    stripeActiveSubscription,
    latestFailedStripePaymentAttempt,
  ]);
};
